import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, X, Mail, Phone, Building2, MapPin, Calendar, Plus, Send, Loader2, Trash2, Play, Pencil, Copy, Tag } from "lucide-react";
import RecordEditDialog, { type EditableTable, type FieldDef } from "@/components/admin/RecordEditDialog";
import CancelSubscriptionDialog from "@/components/admin/CancelSubscriptionDialog";
import CreateSubscriptionSheet from "@/components/admin/CreateSubscriptionSheet";
import CreateInvoiceSheet from "@/components/admin/CreateInvoiceSheet";
import VipDiscountSheet from "@/components/admin/VipDiscountSheet";
import { invoiceFields, orderFields, subscriptionFields, profileFields } from "@/components/admin/recordFields";
import AdminLayout from "@/components/admin/AdminLayout";
import { deliveryWindowLabel } from "@/lib/delivery-dates";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

type Profile = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  phone: string | null;
  status: "pending" | "approved" | "rejected";
  created_at: string;
  approved_at: string | null;
  vitamin_stations: number;
};

type Subscription = {
  id: string;
  status: string;
  tier_tubes: number;
  quantities: Record<string, number>;
  monthly_price_cents: number | null;
  currency: string | null;
  next_delivery_date: string | null;
  paused_until: string | null;
  paused_for_overdue: boolean | null;
  paused_for_overdue_invoice_id: string | null;
  cancel_at_period_end: boolean | null;
  current_period_end: string | null;
  cancelled_at: string | null;
  pending_tier_tubes: number | null;

  pending_quantities: Record<string, number> | null;
  pending_monthly_price_cents: number | null;
  pending_effective_date: string | null;
  shipping_address: Record<string, unknown> | null;
  nickname: string | null;
  is_pilot: boolean;
};

type Order = {
  id: string;
  created_at: string;
  status: string;
  payment_status: string | null;
  total_cents: number | null;
  currency: string | null;
  product_name: string;
  order_mode: string | null;
  quantity: number;
};

const fmtDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("sv-SE", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

const fmtMoney = (cents: number | null, ccy: string | null) =>
  cents != null
    ? new Intl.NumberFormat("sv-SE", {
        style: "currency",
        currency: ccy || "SEK",
        maximumFractionDigits: 0,
      }).format(cents / 100)
    : "—";

const badgeFor = (s: string) => {
  switch (s) {
    case "approved":
    case "active":
    case "paid":
    case "shipped":
      return "bg-emerald-100 text-emerald-800 hover:bg-emerald-100";
    case "pending":
      return "bg-amber-100 text-amber-800 hover:bg-amber-100";
    case "paused":
      return "bg-blue-100 text-blue-800 hover:bg-blue-100";
    case "rejected":
    case "cancelled":
    case "failed":
      return "bg-rose-100 text-rose-800 hover:bg-rose-100";
    default:
      return "bg-slate-100 text-slate-800 hover:bg-slate-100";
  }
};

// Svenska labels så admin ser samma språk som kunden istället för råa
// engelska enum-strängar ("paused", "scheduled", "awaiting_payment").
const paymentStatusLabel = (s: string): string => {
  switch (s) {
    case "paid": return "Betald";
    case "pending": return "Väntar";
    case "scheduled": return "Schemalagd";
    case "awaiting_payment": return "Väntar på betalning";
    case "requires_action": return "Bekräftelse krävs";
    case "failed": return "Misslyckades";
    case "cancelled": return "Avbruten";
    case "refunded": return "Återbetald";
    case "partially_refunded": return "Delvis återbetald";
    case "paused": return "Pausad";
    case "refunded": return "Återbetald";
    default: return s || "—";
  }
};

const orderStatusLabel = (s: string): string => {
  switch (s) {
    case "pending": return "Väntar";
    case "shipped": return "Skickad";
    case "cancelled": return "Avbruten";
    case "paused": return "Pausad";
    default: return s || "—";
  }
};

interface InvoiceRow {
  id: string;
  number: string | null;
  status: string;
  amount_cents: number | null;
  currency: string | null;
  due_date: string | null;
  sent_at: string | null;
  paid_at: string | null;
  subscription_id: string | null;
  order_id: string | null;
  ocr_reference: string | null;
  reminder_count: number | null;
  reminder_1_sent_at: string | null;
  reminder_2_sent_at: string | null;
  reminder_3_sent_at: string | null;
  stripe_status: string | null;
  billing_reason: string | null;
  period_start: string | null;
  period_end: string | null;
  last_synced_at: string | null;
  updated_at?: string | null;
}

const invBillingReason = (reason: string | null): string => {
  const map: Record<string, string> = {
    subscription_create: "Första månaden",
    subscription_cycle: "Månadsförnyelse",
    subscription_update: "Ändrat abonnemang",
    manual: "Manuell faktura",
  };
  return reason ? map[reason] ?? reason : "";
};

const invPeriod = (start: string | null): string => {
  if (!start) return "";
  return new Date(`${start}T00:00:00`).toLocaleDateString("sv-SE", { month: "long", year: "numeric" });
};

interface EmailLogRow {
  id: string;
  template_name: string;
  recipient_email: string;
  status: string;
  error_message: string | null;
  created_at: string;
}

const invoiceStatusLabel = (s: string): string => {
  switch (s) {
    case "draft": return "Utkast";
    case "open": return "Att betala";
    case "paid": return "Betald";
    case "uncollectible": return "Avskriven";
    case "void": return "Makulerad";
    default: return s || "—";
  }
};

const emailTemplateLabel = (t: string): string => {
  switch (t) {
    case "invoice-reminder": return "Påminnelse – obetald faktura";
    case "stripe-invoice-reminder": return "Påminnelse – obetald faktura (Stripe)";
    case "stripe-invoice-sent": return "Faktura skickad (Stripe)";
    case "invoice-overdue-paused": return "Intern notis – prenumeration pausad";
    case "order-confirmation": return "Orderbekräftelse";
    case "shipping-confirmation": return "Leveransbekräftelse";
    default: return t;
  }
};


const fmtDateTime = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString("sv-SE", {
        year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
      })
    : "—";

const AdminCustomerDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [emailLog, setEmailLog] = useState<EmailLogRow[]>([]);
  const [inviteStatus, setInviteStatus] = useState<{
    invited_at: string | null;
    last_sign_in_at: string | null;
    email_confirmed_at: string | null;
  } | null>(null);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [refundOrder, setRefundOrder] = useState<Order | null>(null);
  const [editTarget, setEditTarget] = useState<{
    table: EditableTable;
    title: string;
    fields: FieldDef[];
    record: Record<string, unknown>;
  } | null>(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundNote, setRefundNote] = useState("");
  const [refunding, setRefunding] = useState(false);
  const [resumingSubId, setResumingSubId] = useState<string | null>(null);
  const [cancelSubId, setCancelSubId] = useState<string | null>(null);


  // Radera kund-flöde

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteCounts, setDeleteCounts] = useState<{ subscriptions: number; orders: number; invoices: number } | null>(null);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deletePreviewing, setDeletePreviewing] = useState(false);

  // Vitaminstationer – inline editor
  const [stationsDraft, setStationsDraft] = useState<string>("0");
  const [stationsSaving, setStationsSaving] = useState(false);

  // Skapa-åtgärder direkt från kundkortet
  const [createOpen, setCreateOpen] = useState(false);
  const [createInvoiceOpen, setCreateInvoiceOpen] = useState(false);
  const [copySource, setCopySource] = useState<{ id: string; number: string | null } | null>(null);
  const [vipOpen, setVipOpen] = useState(false);

  const load = async () => {
    if (!id) return;
    const [{ data: p }, { data: s }, { data: o }, { data: inv }, { data: invoiceRows }] = await Promise.all([
      supabase.from("profiles").select("*").eq("id", id).maybeSingle(),
      supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", id)
        .in("status", ["active", "paused"])
        .order("created_at", { ascending: true }),
      supabase
        .from("orders")
        .select("id, created_at, status, payment_status, total_cents, currency, product_name, order_mode, quantity")
        .eq("user_id", id)
        .not("payment_status", "in", "(awaiting_payment,cancelled)")
        .order("created_at", { ascending: false })
        .limit(50),
      (supabase as any).rpc("get_customer_invite_status", { _user_id: id }),
      supabase
        .from("invoices")
        .select(
          "id, number, status, amount_cents, currency, due_date, sent_at, paid_at, subscription_id, order_id, ocr_reference, reminder_count, reminder_1_sent_at, reminder_2_sent_at, reminder_3_sent_at, stripe_status, billing_reason, period_start, period_end, last_synced_at, updated_at",
        )
        .eq("user_id", id)
        .order("created_at", { ascending: false })
        .limit(50),
    ]);
    const profileRow = (p as Profile) || null;
    setProfile(profileRow);
    if (profileRow) setStationsDraft(String(profileRow.vitamin_stations ?? 0));
    setSubscriptions((s as Subscription[]) || []);
    setOrders((o as Order[]) || []);
    setInvoices((invoiceRows as InvoiceRow[]) || []);
    const invRow = Array.isArray(inv) ? (inv as any[])[0] : null;
    setInviteStatus(
      invRow
        ? {
            invited_at: invRow.invited_at ?? null,
            last_sign_in_at: invRow.last_sign_in_at ?? null,
            email_confirmed_at: invRow.email_confirmed_at ?? null,
          }
        : null,
    );
    if (profileRow?.email) {
      const { data: logs } = await supabase
        .from("email_send_log")
        .select("id, template_name, recipient_email, status, error_message, created_at")
        .eq("recipient_email", profileRow.email)
        .order("created_at", { ascending: false })
        .limit(25);
      setEmailLog((logs as EmailLogRow[]) || []);
    }
    setLoading(false);

  };

  const openRefund = (o: Order) => {
    setRefundOrder(o);
    setRefundAmount(o.total_cents != null ? (o.total_cents / 100).toString() : "");
    setRefundNote("");
  };

  const submitRefund = async () => {
    if (!refundOrder) return;
    setRefunding(true);
    try {
      const parsed = parseFloat(refundAmount.replace(",", "."));
      const amountCents = Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
      const { data, error } = await supabase.functions.invoke("admin-refund", {
        body: {
          order_id: refundOrder.id,
          ...(amountCents > 0 ? { amount_cents: amountCents } : {}),
          ...(refundNote.trim() ? { note: refundNote.trim() } : {}),
        },
      });
      if (error) {
        const ctx = (error as { context?: { error?: string } }).context;
        throw new Error(ctx?.error ?? (data as any)?.error ?? error.message);
      }
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success(
        `Återbetalning klar: ${fmtMoney((data as any).amount_cents, refundOrder.currency)}${
          (data as any).stripe_credit_note_id ? " · kreditfaktura skapad" : ""
        }`,
      );
      setRefundOrder(null);
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "Kunde inte återbetala");
    } finally {
      setRefunding(false);
    }
  };

  const handleResumeSubscription = async (subscriptionId: string) => {
    if (resumingSubId) return;
    setResumingSubId(subscriptionId);
    try {
      const { data, error } = await (supabase as any).functions.invoke(
        "admin-resume-subscription",
        { body: { subscription_id: subscriptionId } },
      );
      if (error || !data?.success) {
        throw new Error(error?.message || data?.error || "Kunde inte återuppta");
      }
      toast.success("Prenumeration återupptagen", {
        description: `Nästa leverans planerad till ${fmtDate(data.next_delivery_date)}.`,
      });
      await load();
    } catch (e: any) {
      toast.error(e.message || "Något gick fel");
    } finally {
      setResumingSubId(null);
    }
  };

  const handleSendPasswordReset = async () => {

    if (!id) return;
    setSendingInvite(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-send-password-reset", {
        body: { user_id: id },
      });
      if (error) {
        const ctx = (error as { context?: { error?: string } }).context;
        throw new Error(ctx?.error || error.message || "Kunde inte skicka lösenordsåterställning");
      }
      if (data && typeof data === "object" && "error" in data && data.error) {
        throw new Error(String((data as { error: string }).error));
      }
      toast.success("Lösenordsåterställning skickad", {
        description: `Mejl har skickats till ${profile?.email ?? "kunden"}.`,
      });
    } catch (e) {
      toast.error("Kunde inte skicka lösenordsåterställning", {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setSendingInvite(false);
    }
  };

  const handleSendInvite = async () => {
    if (!id) return;
    setSendingInvite(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-send-customer-invite", {
        body: { user_id: id },
      });
      if (error) {
        const ctx = (error as { context?: { error?: string } }).context;
        throw new Error(ctx?.error || error.message || "Kunde inte skicka inbjudan");
      }
      if (data && typeof data === "object" && "error" in data && data.error) {
        throw new Error(String((data as { error: string }).error));
      }
      const isRecovery = (data as { mode?: string } | null)?.mode === "recovery";
      toast.success(isRecovery ? "Återställningsmejl skickat" : "Inbjudan skickad", {
        description: isRecovery
          ? `Kunden hade redan ett konto — en länk för nytt lösenord skickades till ${profile?.email ?? "kunden"}.`
          : `Mejl har skickats till ${profile?.email ?? "kunden"}.`,
      });
      await load();
    } catch (e) {
      toast.error("Kunde inte skicka inbjudan", {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setSendingInvite(false);
    }
  };

  const openDeleteDialog = async () => {
    if (!id) return;
    setDeleteOpen(true);
    setDeleteConfirmEmail("");
    setDeleteCounts(null);
    setDeletePreviewing(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-delete-customer", {
        body: { user_id: id, preview: true },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      setDeleteCounts((data as any).counts);
    } catch (e) {
      toast.error("Kunde inte hämta sammanfattning", {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setDeletePreviewing(false);
    }
  };

  const handleDeleteCustomer = async () => {
    if (!id || !profile) return;
    setDeleteBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-delete-customer", {
        body: { user_id: id, confirm_email: deleteConfirmEmail.trim() },
      });
      if (error) {
        const ctx = (error as { context?: { error?: string } }).context;
        throw new Error(ctx?.error || error.message || "Kunde inte radera kund");
      }
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success("Kund raderad", {
        description: `${profile.email} och all kopplad data har tagits bort.`,
      });
      setDeleteOpen(false);
      navigate("/admin/customers");
    } catch (e) {
      toast.error("Kunde inte radera kund", {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setDeleteBusy(false);
    }
  };

  const handleSaveStations = async () => {
    if (!profile) return;
    const n = Math.max(0, Math.floor(Number(stationsDraft)));
    if (!Number.isFinite(n)) {
      toast.error("Ange ett giltigt antal");
      return;
    }
    if (n === profile.vitamin_stations) return;
    setStationsSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ vitamin_stations: n })
      .eq("id", profile.id);
    setStationsSaving(false);
    if (error) {
      toast.error("Kunde inte spara antal stationer", { description: error.message });
      return;
    }
    setProfile({ ...profile, vitamin_stations: n });
    setStationsDraft(String(n));
    toast.success(`Antal vitaminstationer uppdaterat (${n})`);
  };


  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const setStatus = async (status: "approved" | "rejected") => {
    if (!profile) return;
    setBusy(true);
    const { error } = await supabase
      .from("profiles")
      .update({ status })
      .eq("id", profile.id);
    setBusy(false);
    if (error) {
      toast.error("Kunde inte uppdatera status");
      return;
    }
    toast.success(status === "approved" ? "Kund godkänd" : "Kund avvisad");
    load();
  };

  if (loading) {
    return (
      <AdminLayout title="Kund">
        <p className="text-sm text-muted-foreground">Laddar…</p>
      </AdminLayout>
    );
  }

  if (!profile) {
    return (
      <AdminLayout title="Kund">
        <p className="text-sm text-muted-foreground">Kund hittades inte.</p>
        <Link to="/admin/customers" className="text-primary text-sm hover:underline mt-4 inline-block">
          ← Tillbaka
        </Link>
      </AdminLayout>
    );
  }

  const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(" ") || "—";

  return (
    <AdminLayout title={fullName}>
      <div className="space-y-6">
        <Link
          to="/admin/customers"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Alla kunder
        </Link>

        {inviteStatus && inviteStatus.invited_at === null && inviteStatus.last_sign_in_at === null && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-amber-900">
              <div className="font-semibold">Den här kunden har inte fått sin inbjudan än.</div>
              <div className="text-xs mt-0.5">
                Kontot är skapat men inget mejl har skickats. Skicka inbjudan när du är klar med
                prenumeration och övrig information.
              </div>
            </div>
            <Button
              size="sm"
              onClick={handleSendInvite}
              disabled={sendingInvite}
              className="shrink-0"
            >
              {sendingInvite ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Send className="h-3.5 w-3.5 mr-1.5" />
              )}
              Skicka inbjudan nu
            </Button>
          </div>
        )}

        {orders.some((o) => o.payment_status === "failed") && (
          <div className="mb-6 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900">
            <span className="font-semibold">Misslyckad betalning.</span>{" "}
            Senaste kortdragningen för den här kunden misslyckades. Kunden har fått ett mejl med uppmaning att uppdatera sina kortuppgifter. Om alla omförsök misslyckas pausas prenumerationen automatiskt.
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profil */}
          <Card className="lg:col-span-1">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold">Profil</CardTitle>
              <div className="flex items-center gap-2">
                <Badge className={badgeFor(profile.status)}>{profile.status}</Badge>
                <Button
                  size="sm"
                  variant="ghost"
                  title="Redigera kunduppgifter manuellt"
                  onClick={() =>
                    setEditTarget({
                      table: "profiles",
                      title: "Redigera kunduppgifter",
                      fields: profileFields,
                      record: profile as unknown as Record<string, unknown>,
                    })
                  }
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <div className="text-xs uppercase text-muted-foreground tracking-wide">Namn</div>
                <div className="text-foreground">{fullName}</div>
              </div>
              {profile.company && (
                <div className="flex items-start gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <span>{profile.company}</span>
                </div>
              )}
              <div className="flex items-start gap-2">
                <Mail className="h-4 w-4 text-muted-foreground mt-0.5" />
                <a href={`mailto:${profile.email}`} className="hover:underline break-all">
                  {profile.email}
                </a>
              </div>
              {profile.phone && (
                <div className="flex items-start gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <a href={`tel:${profile.phone}`} className="hover:underline">
                    {profile.phone}
                  </a>
                </div>
              )}
              <div className="flex items-start gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
                <span className="text-muted-foreground">
                  Registrerad {fmtDate(profile.created_at)}
                  {profile.approved_at && ` · Godkänd ${fmtDate(profile.approved_at)}`}
                </span>
              </div>

              {(() => {
                const hasSignedIn = !!inviteStatus?.last_sign_in_at;
                const isConfirmed = !!inviteStatus?.email_confirmed_at;
                const hasInvite = !!inviteStatus?.invited_at;
                // "active" = kontot är användbart (bekräftat eller inloggat).
                // För dessa fungerar bara lösenordsåterställning – inte ny invite.
                const state: "never_invited" | "invited_pending" | "active" =
                  hasSignedIn || isConfirmed
                    ? "active"
                    : hasInvite
                    ? "invited_pending"
                    : "never_invited";

                const statusText = !inviteStatus
                  ? "Hämtar kontostatus…"
                  : hasSignedIn
                  ? `Senast inloggad ${fmtDate(inviteStatus.last_sign_in_at!)}`
                  : isConfirmed
                  ? "Kontot är bekräftat – skicka lösenordsåterställning om kunden saknar lösenord."
                  : hasInvite
                  ? `Inbjudan skickad ${fmtDate(inviteStatus.invited_at!)} – ej inloggad än`
                  : "Inget mejl har skickats än.";

                const buttonLabel =
                  state === "active"
                    ? "Skicka lösenordsåterställning"
                    : state === "invited_pending"
                    ? "Skicka inbjudan igen"
                    : "Skicka inbjudan";

                const onClick =
                  state === "active" ? handleSendPasswordReset : handleSendInvite;

                return (
                  <div className="pt-3 border-t border-border space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                      Kontoåtgärder
                    </Label>
                    <div className="text-xs text-muted-foreground">{statusText}</div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={onClick}
                      disabled={sendingInvite}
                      className="w-full"
                    >
                      {sendingInvite ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <Send className="h-3.5 w-3.5 mr-1.5" />
                      )}
                      {buttonLabel}
                    </Button>
                  </div>
                );
              })()}
              <div className="pt-3 border-t border-border space-y-1.5">
                <Label htmlFor="vitamin-stations" className="text-xs uppercase tracking-wide text-muted-foreground">
                  Vitaminstationer
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="vitamin-stations"
                    type="number"
                    min={0}
                    step={1}
                    inputMode="numeric"
                    value={stationsDraft}
                    onChange={(e) => setStationsDraft(e.target.value)}
                    onBlur={handleSaveStations}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                    disabled={stationsSaving}
                    className="h-9 w-24"
                  />
                  <span className="text-xs text-muted-foreground">
                    {stationsSaving ? "Sparar…" : "st hos kunden"}
                  </span>
                </div>
              </div>

              {profile.status === "pending" && (
                <div className="pt-3 border-t border-border flex gap-2">
                  <Button
                    size="sm"
                    disabled={busy}
                    onClick={() => setStatus("approved")}
                    className="flex-1"
                  >
                    <Check className="h-3.5 w-3.5 mr-1" /> Godkänn
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => setStatus("rejected")}
                    className="flex-1"
                  >
                    <X className="h-3.5 w-3.5 mr-1" /> Avvisa
                  </Button>
                </div>
              )}
              {profile.status === "approved" && (
                <div className="pt-3 border-t border-border">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => setStatus("rejected")}
                  >
                    Återkalla godkännande
                  </Button>
                </div>
              )}
              {profile.status === "rejected" && (
                <div className="pt-3 border-t border-border">
                  <Button size="sm" disabled={busy} onClick={() => setStatus("approved")}>
                    Godkänn ändå
                  </Button>
                </div>
              )}

              <div className="pt-3 border-t border-border">
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full mb-2"
                  onClick={() => setVipOpen(true)}
                >
                  <Tag className="h-3.5 w-3.5 mr-1.5" /> Ge rabatt
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openDeleteDialog}
                  className="w-full text-rose-600 border-rose-300 hover:bg-rose-50 hover:text-rose-700"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Radera kund permanent
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Prenumerationer (en per anläggning) */}
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold">
                Prenumerationer ({subscriptions.length})
              </CardTitle>
              <Button size="sm" variant="outline" onClick={() => setCreateOpen(true)}>
                <Plus className="h-3.5 w-3.5 mr-1" /> Ny anläggning
              </Button>
            </CardHeader>
            <CardContent className="text-sm space-y-4">
              {subscriptions.length === 0 ? (
                <p className="text-muted-foreground">Ingen aktiv prenumeration.</p>
              ) : (
                subscriptions.map((sub) => {
                  const addr = sub.shipping_address as
                    | { line1?: string; line2?: string; address?: string; postal_code?: string; postalCode?: string; city?: string; country?: string }
                    | null;
                  return (
                    <div key={sub.id} className="rounded-lg border border-border p-4 space-y-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className={
                          sub.paused_for_overdue || sub.cancel_at_period_end
                            ? "bg-rose-100 text-rose-800 hover:bg-rose-100"
                            : badgeFor(sub.status)
                        }>
                          {sub.paused_for_overdue
                            ? "Pausad – förfallen"
                            : sub.cancel_at_period_end
                              ? "Uppsagd"
                              : sub.status}
                        </Badge>
                        {sub.is_pilot && (

                          <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100 text-[10px] uppercase tracking-wider">
                            Pilot
                          </Badge>
                        )}
                        {sub.nickname && (
                          <Badge variant="outline" className="text-xs">{sub.nickname}</Badge>
                        )}
                        {sub.status !== "cancelled" && !sub.cancel_at_period_end && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="ml-auto text-rose-700 hover:text-rose-800"
                            onClick={() => setCancelSubId(sub.id)}
                          >
                            Avsluta prenumeration
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className={
                            sub.status !== "cancelled" && !sub.cancel_at_period_end ? "" : "ml-auto"
                          }
                          title="Redigera abonnemanget manuellt"
                          onClick={() =>
                            setEditTarget({
                              table: "subscriptions",
                              title: "Redigera abonnemang",
                              fields: subscriptionFields,
                              record: sub as unknown as Record<string, unknown>,
                            })
                          }
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <span className="text-foreground font-medium">{sub.tier_tubes} tuber/mån</span>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-foreground">
                          {sub.is_pilot ? "Gratis" : `${fmtMoney(sub.monthly_price_cents, sub.currency)}/mån`}
                        </span>
                        <span className="text-muted-foreground">·</span>
                        {sub.cancel_at_period_end ? (
                          <span className="text-muted-foreground text-xs">
                            Sista perioden löper till {fmtDate(sub.current_period_end)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-xs">
                            Nästa: {fmtDate(sub.next_delivery_date)}
                            {sub.next_delivery_date && (
                              <> (levereras {deliveryWindowLabel(sub.next_delivery_date)})</>
                            )}
                          </span>
                        )}
                      </div>

                      {sub.pending_tier_tubes != null && (
                        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900 text-xs">
                          Planerad ändring: {sub.pending_tier_tubes} tuber
                          {sub.pending_monthly_price_cents != null &&
                            ` · ${fmtMoney(sub.pending_monthly_price_cents, sub.currency)}/mån`}
                          {sub.pending_effective_date &&
                            ` · träder i kraft ${fmtDate(sub.pending_effective_date)}`}
                        </div>
                      )}

                      {sub.cancel_at_period_end && (
                        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-rose-900 text-xs">
                          Kunden har sagt upp abonnemanget
                          {sub.cancelled_at && ` ${fmtDate(sub.cancelled_at)}`}
                          {sub.current_period_end
                            ? ` — det avslutas ${fmtDate(sub.current_period_end)}.`
                            : "."}
                        </div>
                      )}

                      {sub.paused_for_overdue ? (
                        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-rose-900 text-xs space-y-2">
                          <p>
                            Pausad på grund av en obetald faktura.
                            {sub.paused_for_overdue_invoice_id && (
                              <> <Link to={`/admin/invoices?highlight=${sub.paused_for_overdue_invoice_id}`} className="underline">Se faktura</Link>.</>
                            )}
                          </p>
                          {(() => {
                            const pausedInv = invoices.find((i) => i.id === sub.paused_for_overdue_invoice_id);
                            if (!pausedInv) return null;
                            const last =
                              pausedInv.reminder_3_sent_at || pausedInv.reminder_2_sent_at || pausedInv.reminder_1_sent_at;
                            return (
                              <p className="text-[11px]">
                                Faktura {pausedInv.number || "—"} · {fmtMoney(pausedInv.amount_cents, pausedInv.currency)} ·
                                {" "}förföll {fmtDate(pausedInv.due_date)} ·{" "}
                                {pausedInv.reminder_count ?? 0} påminnelse(r)
                                {last ? `, senast ${fmtDateTime(last)}` : ""}
                              </p>
                            );
                          })()}

                          <button
                            onClick={() => handleResumeSubscription(sub.id)}
                            disabled={resumingSubId === sub.id}
                            className="inline-flex items-center gap-1 rounded-md bg-rose-900 px-2 py-1 text-white text-[10px] uppercase tracking-wider hover:bg-rose-800 disabled:opacity-50"
                          >
                            <Play className="h-3 w-3" />
                            {resumingSubId === sub.id ? "Återupptar…" : "Återuppta prenumeration"}
                          </button>
                        </div>
                      ) : sub.paused_until ? (
                        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900 text-xs">
                          Pausad till {fmtDate(sub.paused_until)}
                        </div>
                      ) : null}


                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {Object.entries(sub.quantities || {}).filter(([_, v]) => Number(v) > 0).map(([k, v]) => (
                          <div key={k} className="rounded-md border border-border px-2 py-1.5 text-xs">
                            <div className="text-muted-foreground truncate">{k}</div>
                            <div className="font-medium text-foreground">{String(v)} st</div>
                          </div>
                        ))}
                      </div>

                      {addr && (
                        <div className="pt-2 border-t border-border flex items-start gap-2 text-xs">
                          <MapPin className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                          <div className="text-foreground">
                            <div>{addr.address || addr.line1}</div>
                            {addr.line2 && <div>{addr.line2}</div>}
                            <div className="text-muted-foreground">
                              {addr.postal_code || addr.postalCode} {addr.city}
                              {addr.country ? `, ${addr.country}` : ""}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        </div>

        {/* Orderhistorik */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold">
              Orderhistorik ({orders.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Datum</TableHead>
                  <TableHead>Produkt</TableHead>
                  <TableHead>Typ</TableHead>
                  <TableHead>Antal</TableHead>
                  <TableHead>Belopp</TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead>Betalning</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">
                      Inga ordrar än.
                    </TableCell>
                  </TableRow>
                ) : (
                  orders.map((o) => (
                    <TableRow key={o.id}>
                      <TableCell className="text-sm">{fmtDate(o.created_at)}</TableCell>
                      <TableCell className="text-sm">{o.product_name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {o.order_mode || "—"}
                      </TableCell>
                      <TableCell className="text-sm">{o.quantity}</TableCell>
                      <TableCell className="text-sm">{fmtMoney(o.total_cents, o.currency)}</TableCell>
                      <TableCell>
                        <Badge className={badgeFor(o.status)}>{orderStatusLabel(o.status)}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={badgeFor(o.payment_status || "")}>
                          {paymentStatusLabel(o.payment_status || "")}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Redigera ordern manuellt"
                          onClick={() =>
                            setEditTarget({
                              table: "orders",
                              title: "Redigera order",
                              fields: orderFields,
                              record: o as unknown as Record<string, unknown>,
                            })
                          }
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        {(o.payment_status === "paid" || o.payment_status === "partially_refunded") && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                            onClick={() => openRefund(o)}
                          >
                            Återbetala
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Fakturor och påminnelser */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-semibold">
              Fakturor och påminnelser ({invoices.length})
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setCopySource(null);
                setCreateInvoiceOpen(true);
              }}
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Ny faktura
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Faktura</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Belopp</TableHead>
                  <TableHead>Förfaller</TableHead>
                  <TableHead>Påminnelser</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">
                      Inga fakturor än.
                    </TableCell>
                  </TableRow>
                ) : (
                  invoices.map((inv) => {
                    const reminders = [
                      { n: 1, at: inv.reminder_1_sent_at },
                      { n: 2, at: inv.reminder_2_sent_at },
                      { n: 3, at: inv.reminder_3_sent_at },
                    ].filter((r) => r.at);
                    return (
                      <TableRow key={inv.id}>
                        <TableCell className="text-sm">
                          <div className="font-mono text-xs">{inv.number || "—"}</div>
                          {inv.ocr_reference && (
                            <div className="text-[10px] text-muted-foreground">OCR: {inv.ocr_reference}</div>
                          )}
                          {(inv.subscription_id || inv.billing_reason) && (
                            <div className="text-[10px] text-muted-foreground">
                              {inv.subscription_id ? "Prenumerationsfaktura" : "Engångsköp"}
                              {inv.billing_reason ? ` · ${invBillingReason(inv.billing_reason)}` : ""}
                              {inv.period_start ? ` · ${invPeriod(inv.period_start)}` : ""}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={badgeFor(inv.status)}>{invoiceStatusLabel(inv.status)}</Badge>
                          {inv.stripe_status && inv.stripe_status !== inv.status &&
                            inv.last_synced_at &&
                            (!inv.updated_at ||
                              new Date(inv.last_synced_at) >= new Date(inv.updated_at)) && (
                            <div className="text-[10px] text-amber-700 mt-1">Stripe: {inv.stripe_status}</div>
                          )}
                          {inv.last_synced_at && (
                            <div className="text-[10px] text-muted-foreground mt-0.5">
                              Avstämd {fmtDateTime(inv.last_synced_at)}
                            </div>
                          )}
                        </TableCell>

                        <TableCell className="text-sm">{fmtMoney(inv.amount_cents, inv.currency)}</TableCell>
                        <TableCell className="text-sm">{fmtDate(inv.due_date)}</TableCell>
                        <TableCell className="text-xs">
                          {reminders.length === 0 ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            <div className="space-y-0.5">
                              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                                {reminders.length} skickad{reminders.length === 1 ? "" : "e"}
                              </Badge>
                              {reminders.map((r) => (
                                <div key={r.n} className="text-[10px] text-muted-foreground">
                                  Påminnelse {r.n}: {fmtDateTime(r.at)}
                                </div>
                              ))}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Redigera fakturan manuellt"
                            onClick={() =>
                              setEditTarget({
                                table: "invoices",
                                title: "Redigera faktura",
                                fields: invoiceFields,
                                record: inv as unknown as Record<string, unknown>,
                              })
                            }
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Skapa en ny faktura med samma rader – ändra det du vill"
                            onClick={() => {
                              setCopySource({ id: inv.id, number: inv.number });
                              setCreateInvoiceOpen(true);
                            }}
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                          <Link
                            to={`/admin/invoices?highlight=${inv.id}`}
                            className="text-xs underline text-muted-foreground hover:text-foreground"
                          >
                            Öppna
                          </Link>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Skickade mejl */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold">
              Skickade mejl ({emailLog.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tidpunkt</TableHead>
                  <TableHead>Typ</TableHead>
                  <TableHead>Mottagare</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {emailLog.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-sm text-muted-foreground py-8">
                      Inga mejl loggade än.
                    </TableCell>
                  </TableRow>
                ) : (
                  emailLog.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="text-sm">{fmtDateTime(e.created_at)}</TableCell>
                      <TableCell className="text-sm">{emailTemplateLabel(e.template_name)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground break-all">{e.recipient_email}</TableCell>
                      <TableCell>
                        <Badge
                          className={
                            e.status === "sent"
                              ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
                              : e.status === "failed"
                                ? "bg-rose-100 text-rose-800 hover:bg-rose-100"
                                : "bg-slate-100 text-slate-800 hover:bg-slate-100"
                          }
                        >
                          {e.status === "sent" ? "Skickat" : e.status === "failed" ? "Misslyckades" : "I kö"}
                        </Badge>
                        {e.error_message && (
                          <div className="text-[10px] text-rose-700 mt-0.5">{e.error_message}</div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>


      <Dialog open={!!refundOrder} onOpenChange={(open) => !open && setRefundOrder(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Återbetala beställning</DialogTitle>
            <DialogDescription>
              {refundOrder?.product_name} · {fmtMoney(refundOrder?.total_cents ?? null, refundOrder?.currency ?? null)}.
              Pengarna skickas tillbaka via betalleverantören och en kreditfaktura skapas automatiskt när
              beställningen har en faktura.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="refund-amount">Belopp (kr) – lämna fullt belopp för hel återbetalning</Label>
              <Input
                id="refund-amount"
                inputMode="decimal"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="refund-note">Anteckning (valfritt, syns internt)</Label>
              <Input
                id="refund-note"
                value={refundNote}
                onChange={(e) => setRefundNote(e.target.value)}
                placeholder="T.ex. trasig tub, reklamation #12"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundOrder(null)} disabled={refunding}>
              Avbryt
            </Button>
            <Button
              variant="destructive"
              onClick={submitRefund}
              disabled={refunding}
            >
              {refunding ? "Återbetalar…" : "Genomför återbetalning"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CreateSubscriptionSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        userId={id}
        customerName={fullName}
        onCreated={load}
      />

      <CreateInvoiceSheet
        open={createInvoiceOpen}
        onOpenChange={(o) => {
          setCreateInvoiceOpen(o);
          if (!o) setCopySource(null);
        }}
        userId={id}
        customerName={fullName}
        sourceInvoiceId={copySource?.id}
        sourceInvoiceNumber={copySource?.number ?? null}
        onCreated={load}
      />

      <VipDiscountSheet
        open={vipOpen}
        onOpenChange={setVipOpen}
        presetUser={
          profile
            ? {
                id: profile.id,
                email: profile.email,
                first_name: profile.first_name,
                last_name: profile.last_name,
              }
            : null
        }
      />

      <Dialog open={deleteOpen} onOpenChange={(o) => !deleteBusy && setDeleteOpen(o)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-rose-700">Radera kund permanent</DialogTitle>
            <DialogDescription>
              Detta tar bort {profile.email} och all kopplad data. Åtgärden går inte att ångra.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-rose-900">
              {deletePreviewing ? (
                <div className="flex items-center gap-2 text-xs">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Räknar data…
                </div>
              ) : deleteCounts ? (
                <ul className="text-xs space-y-1">
                  <li>Profil och inloggningskonto</li>
                  <li>{deleteCounts.subscriptions} prenumeration(er)</li>
                  <li>{deleteCounts.orders} order(s) / orderhistorik</li>
                  <li>{deleteCounts.invoices} faktura(or)</li>
                  <li>Rabattkopplingar och roller</li>
                </ul>
              ) : (
                <p className="text-xs">Kunde inte hämta sammanfattning.</p>
              )}
              <p className="text-[11px] mt-2 text-rose-800">
                Stripe-objekt rensas inte automatiskt — städa vid behov i Stripe-dashboarden.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="delete-confirm-email" className="text-xs">
                Bekräfta genom att skriva kundens mejladress: <span className="font-mono">{profile.email}</span>
              </Label>
              <Input
                id="delete-confirm-email"
                value={deleteConfirmEmail}
                onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                placeholder={profile.email}
                disabled={deleteBusy}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={deleteBusy}>
              Avbryt
            </Button>
            <Button
              onClick={handleDeleteCustomer}
              disabled={
                deleteBusy ||
                deletePreviewing ||
                deleteConfirmEmail.trim().toLowerCase() !== profile.email.toLowerCase()
              }
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {deleteBusy ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Raderar…
                </>
              ) : (
                <>
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Radera permanent
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RecordEditDialog
        open={!!editTarget}
        onOpenChange={(open) => !open && setEditTarget(null)}
        table={editTarget?.table ?? "profiles"}
        title={editTarget?.title ?? ""}
        fields={editTarget?.fields ?? []}
        record={editTarget?.record ?? null}
        onSaved={load}
      />

      <CancelSubscriptionDialog
        open={!!cancelSubId}
        onOpenChange={(open) => !open && setCancelSubId(null)}
        subscriptionId={cancelSubId}
        onCancelled={() => {
          setCancelSubId(null);
          load();
        }}
      />
    </AdminLayout>
  );
};

export default AdminCustomerDetailPage;
